import { useState, useEffect } from 'react'
import { SystemParameter } from '@/types'
import '../styles/TabStyles.css'

interface OrderLotsTabProps {
  parameters: SystemParameter[]
  onUpdate: (key: string, value: string) => Promise<void>
}

export default function OrderLotsTab({ parameters, onUpdate }: OrderLotsTabProps) {
  const [lots, setLots] = useState<number[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [formValue, setFormValue] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load lots from parameters
  useEffect(() => {
    const param = parameters.find(p => p.param_key === 'order_lot_tiers')
    if (param) {
      try {
        const parsed = JSON.parse(param.param_value)
        setLots(Array.isArray(parsed) ? parsed : [])
      } catch (err) {
        setError('解析下单手数档位失败')
      }
    }
  }, [parameters])

  const handleAddLot = () => {
    setEditingIndex(null)
    setFormValue(0)
    setShowModal(true)
  }

  const handleEditLot = (index: number) => {
    setEditingIndex(index)
    setFormValue(lots[index])
    setShowModal(true)
  }

  const handleDeleteLot = async (index: number) => {
    if (!confirm('确定要删除这个手数档位吗？')) return

    const newLots = lots.filter((_, i) => i !== index)
    setLoading(true)
    try {
      await onUpdate('order_lot_tiers', JSON.stringify(newLots))
      setLots(newLots)
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveLot = async () => {
    // Validation
    if (formValue <= 0) {
      setError('手数必须大于0')
      return
    }

    let newLots = [...lots]
    if (editingIndex !== null) {
      newLots[editingIndex] = formValue
    } else {
      newLots.push(formValue)
    }

    // Sort and remove duplicates
    newLots = Array.from(new Set(newLots)).sort((a, b) => a - b)

    if (newLots.length !== lots.length + (editingIndex === null ? 1 : 0)) {
      setError('手数不能重复')
      return
    }

    setLoading(true)
    try {
      await onUpdate('order_lot_tiers', JSON.stringify(newLots))
      setLots(newLots)
      setShowModal(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <h3>下单手数档位</h3>
        <button className="btn-primary" onClick={handleAddLot}>
          + 添加手数
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {lots.length === 0 ? (
        <div className="empty-state">
          <p>暂无下单手数档位</p>
        </div>
      ) : (
        <div className="lots-grid">
          {lots.map((lot, index) => (
            <div key={index} className="lot-card">
              <div className="lot-value">{lot}</div>
              <div className="lot-label">手</div>
              <div className="lot-actions">
                <button
                  className="btn-small btn-edit"
                  onClick={() => handleEditLot(index)}
                >
                  编辑
                </button>
                <button
                  className="btn-small btn-delete"
                  onClick={() => handleDeleteLot(index)}
                  disabled={loading}
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{editingIndex !== null ? '编辑手数' : '添加手数'}</h4>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>手数</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formValue}
                  onChange={e => setFormValue(parseFloat(e.target.value))}
                  placeholder="输入手数"
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                取消
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveLot}
                disabled={loading}
              >
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
